import { useDeleteFact, useGetFacts } from "../../../queries/facts";
import 'react-toastify/dist/ReactToastify.css';
import { toast, ToastContainer } from "react-toastify";
import { useGetEventList } from '../../../queries/event';
import TanStackTable from '../../../components/TanStackTable';
import { createColumnHelper } from '@tanstack/react-table';
import Chip from '../../../components/Chip';
import { useNavigate } from 'react-router-dom';
import { FaTrash } from 'react-icons/fa';
import { useMemo, useState, useEffect } from 'react';
import { ActionButton } from '../../../components/Forms/ActionButton';
const EditIcon = require("../../../utils/media/icons/edit_icon.png");

const FactList = () => {

  const navigate = useNavigate();
  const { data, isPending } = useGetFacts();
  const { mutateAsync: deleteFact, isPending: isDeleting } = useDeleteFact(data?._id);
  const columnHelper = createColumnHelper();

  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState({
    status: '',
    title: '',
    homepage: '',
  });

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    let filtered = data;
    if (filters.status) {
      const isActive = filters.status === 'active';
      filtered = filtered.filter((fact) => fact.isActive === isActive);
    }
    if (filters.homepage) {
      const showInHomePage = filters.homepage === 'active';
      filtered = filtered.filter((fact) => fact.showInHomePage === showInHomePage);
    }
    if (filters.title) {
      filtered = filtered.filter((fact) =>
        fact.title.toLowerCase().includes(filters.title.toLowerCase())
      );
    }
    setFilteredData(filtered);
  };

  useEffect(() => {
    if (data) {
      setFilteredData(data);
    }
  }, [data]);

  const handleDelete = async (id) => {
    deleteFact(id, {
      onSettled: () => {
        toast.success('Fact deleted successfully');
      },
      onError: (error) => {
        toast.error(`Error deleting fact!`);
      },
    });
  }

  // Create columns definition at the top level
  const columns = useMemo(() => [
    columnHelper.display({
      id: 'srno',
      header: 'Sr No.',
      cell: info => info.row.index + 1,
    }),
    columnHelper.accessor('imageUrl', {
      header: 'Icon',
      cell: info => (
        info.getValue() ? (
          <img className="size-5 rounded-full"
            src={info.getValue()}
          />
        ) : <div className="size-5 bg-gray-200 rounded-full flex items-center justify-center">-</div>
      ),
    }),
    columnHelper.accessor('title', {
      header: 'Title',
    }),
    columnHelper.accessor('description', {
      header: 'Description',
      cell: info => (
        <div className="whitespace-normal break-words line-clamp-2">
          {info.getValue() || 'N/A'}
        </div>
      ),
    }),
    columnHelper.accessor('connectedChallenge', {
      header: 'Challenge',
      cell: info => (
        <div className="whitespace-normal break-words">
          {info.getValue()?.name || 'N/A'}
        </div>
      ),
    }),
    columnHelper.accessor('isActive', {
      header: () => <div className="text-center">Status</div>,
      size: 10,
      cell: info => (
        <Chip value={info.getValue() ? 'Active' : 'Inactive'} color={info.getValue() ? 'green' : 'inactive'}  />
      ),
    }),
    columnHelper.accessor('showInHomePage', {
      header: () => <div className="text-center" title="Show this fact in homepage">Homepage?</div>,
      size: 10,
      cell: info => <Chip value={info.getValue() ? 'Yes' : 'No'} color={info.getValue() ? 'green' : 'inactive'} />,
    }),
    columnHelper.accessor('actions', {
      header: () => <div className="text-center">Actions</div>,
      enableColumnFilter: false,
      cell: info => {
        return (
          <div className='flex items-center justify-center'>
            <button 
              className="rounded p-1.5 bg-[rgba(52,199,89,0.1)] hover:bg-[rgba(52,199,89,0.2)] mx-1 min-h-7 min-w-7"
              onClick={() => navigate('/fact-details/create-fact', { state: { factId: info.row.original?._id } })}
            >
              <img src={EditIcon} alt="view" />
            </button>
            <button
              className="rounded p-1.5 bg-[rgba(199,52,52,0.1)] hover:bg-[rgba(199,52,52,0.2)] mx-1 min-h-7"
              onClick={() => {
                handleDelete(info.row.original._id);
              }}
            >
              <FaTrash className="fill-chipRed" />
            </button>
          </div>
        )
      },
    }),
  ], [navigate]);

  if (isPending) {
    return (
      <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">
        <section className="flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
          <div className="flex flex-col pr-px pl-6 w-full max-md:pl-5 max-md:max-w-full">
            <h1 className="self-start text-lg font-semibold">List of Facts</h1>
            <p>Loading...</p>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">
      {/* Filters */}
      <form
        className="flex flex-col pt-5 w-full bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full"
        onSubmit={handleSearch}
      >
        <div className='flex items-center justify-between pr-5'>
          <div className="flex gap-1.5 items-start my-auto text-base whitespace-nowrap ml-5">
            <img
              loading="lazy"
              src="https://cdn.builder.io/api/v1/image/assets/TEMP/4dd39feef0ff4934d9e2e86b8c7285d5d7532aeb26adcdc1e878d021439e2681?placeholderIfAbsent=true&apiKey=d62bea31e5c5472d8bb4326e92b00478"
              alt=""
              className="object-contain shrink-0 w-5 aspect-square"
            />
            <span className="self-stretch">Filters</span>
            <img
              loading="lazy"
              src="https://cdn.builder.io/api/v1/image/assets/TEMP/d18f51a0b2d597a302f55b77fc1071043eab5f274a1cc9e9b3fe3e2c97520a8c?placeholderIfAbsent=true&apiKey=d62bea31e5c5472d8bb4326e92b00478"
              alt=""
              className="object-contain shrink-0 aspect-square w-[18px]"
            />
          </div>
          <ActionButton
            variant={'reset'}
            text={'Create New Fact'}
            handleSubmit={() => navigate('/fact-details/create-fact')}
          />
        </div>
        <div className="flex flex-wrap gap-4 p-5">
          <div className='flex gap-3 w-full'>
            <div className="flex-col w-full">
              <label htmlFor="status" className="gap-1 self-stretch w-full text-gray-900 text-base">
                Status
              </label>
              <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="flex-1 shrink self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
            <div className="flex-col w-full">
              <label htmlFor="homepage" className="gap-1 self-stretch w-full text-gray-900 text-base">
                Homepage
              </label>
              <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                <select
                  name="homepage"
                  value={filters.homepage}
                  onChange={handleFilterChange}
                  className="flex-1 shrink self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base"
                >
                  <option value="">All Homepage</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
            <div className="flex-col w-full">
              {/* Empty column for spacing */}
            </div>
          </div>
          <div className='grid gap-3 grid-cols-1 w-full'>
            <div className="flex-col w-full">
              <label htmlFor="title" className="gap-1 self-stretch w-full text-gray-900 text-base">
                Fact Title
              </label>
              <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                <input
                  type="text"
                  name="title"
                  value={filters.title}
                  onChange={handleFilterChange}
                  placeholder="Enter Fact Title"
                  className='outline-none focus:ring-0 w-full'
                />
              </div>
            </div>
          </div>
        </div>
        <div className='flex gap-3 items-center justify-end p-5'>
          <ActionButton
            variant={'reset'}
            text={'Reset'}
            handleSubmit={() => {
              setFilters({
                status: '',
                title: '',
                homepage: '',
              });
              setFilteredData(data);
            }}
          />
          <ActionButton
            variant={'submit'}
            text={'Search'}
            handleSubmit={handleSearch}
          />
        </div>
      </form>

      <section className="flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
        <div className="flex flex-col pr-px w-full max-md:max-w-full">
          <h1 className="self-start text-lg font-bold pl-6 pb-4">List of Facts</h1>
          <div className="pl-5">
            <TanStackTable
              data={filteredData || data}
              columns={columns}
              enablePagination={true}
            />
          </div>
        </div>
      </section>
      <ToastContainer
        position="top-right"
        autoClose={3000}
      />
    </main>
  )
}

export default FactList;